import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@loot-os/ui/components/alert-dialog";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Plus, TriangleAlert, X } from "lucide-react";
import { useState } from "react";

import { ModeBadge } from "../components/badges";
import { formatDate } from "../lib/format";
import { useStorefrontConfigQuery } from "../lib/storefront";
import { useTRPC } from "../lib/trpc";

export function KeysPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const keys = useQuery(trpc.apiKeys.list.queryOptions());

  const [showCreate, setShowCreate] = useState(false);
  const [type, setType] = useState<"secret" | "publishable">("secret");
  const [name, setName] = useState("");
  const [revealed, setRevealed] = useState<{ plaintext: string; name: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [editingOrigins, setEditingOrigins] = useState<string | null>(null);
  const [origins, setOrigins] = useState<string[]>([]);
  const [newOrigin, setNewOrigin] = useState("");

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: trpc.apiKeys.list.queryOptions().queryKey });
  };

  const create = useMutation(
    trpc.apiKeys.create.mutationOptions({
      onSuccess: (result) => {
        invalidate();
        setRevealed({ plaintext: result.plaintext, name: result.key.name });
        setCopied(false);
        setName("");
      },
    }),
  );
  const revoke = useMutation(trpc.apiKeys.revoke.mutationOptions({ onSuccess: invalidate }));
  const updateOrigins = useMutation(
    trpc.apiKeys.updateOrigins.mutationOptions({
      onSuccess: () => {
        invalidate();
        setEditingOrigins(null);
      },
    }),
  );

  const copy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const closeCreateDialog = () => {
    setShowCreate(false);
    setRevealed(null);
  };

  const addOrigin = () => {
    const trimmed = newOrigin.trim();
    if (!trimmed) return;
    setOrigins((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
    setNewOrigin("");
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">API keys</h1>
          <p className="text-sm text-muted-foreground">
            Secret keys live on your server; publishable keys are browser-safe and origin-locked.
          </p>
        </div>
        <Button type="button" onClick={() => setShowCreate(true)}>
          <Plus />
          Create key
        </Button>
      </div>

      <StorefrontKeyCard />

      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Key</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Origins</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.data?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                    No API keys yet. Create a secret key for your server and a publishable key for
                    the browser.
                  </TableCell>
                </TableRow>
              )}
              {keys.data?.map((key) => (
                <TableRow key={key.id} className={key.revokedAt ? "opacity-50" : undefined}>
                  <TableCell>
                    {key.name || <span className="text-muted-foreground">unnamed</span>}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{key.prefix}…</TableCell>
                  <TableCell>
                    <Badge variant="outline">{key.type === "secret" ? "sk" : "pk"}</Badge>
                  </TableCell>
                  <TableCell>
                    <ModeBadge mode={key.mode} />
                  </TableCell>
                  <TableCell>
                    {key.type !== "publishable" ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingOrigins(key.id);
                          setOrigins(key.allowedOrigins);
                          setNewOrigin("");
                          updateOrigins.reset();
                        }}
                        disabled={!!key.revokedAt}
                        title="Exact origins allowed to use this key from a browser. Empty = allow all."
                      >
                        {key.allowedOrigins.length > 0 ? (
                          `${key.allowedOrigins.length} origin${key.allowedOrigins.length > 1 ? "s" : ""}`
                        ) : (
                          <>
                            any origin
                            <TriangleAlert className="text-amber-400" />
                          </>
                        )}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(key.lastUsedAt)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(key.createdAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    {key.revokedAt ? (
                      <Badge variant="destructive">revoked</Badge>
                    ) : (
                      <AlertDialog>
                        <AlertDialogTrigger
                          render={
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              disabled={revoke.isPending}
                            />
                          }
                        >
                          Revoke
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              Revoke <span className="font-mono">{key.prefix}…</span>?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              Requests signed with this key will stop working immediately. This
                              cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => revoke.mutate({ keyId: key.id })}>
                              Revoke key
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog
        open={showCreate}
        onOpenChange={(open) => {
          if (!open) closeCreateDialog();
        }}
      >
        <DialogContent>
          {revealed ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  {revealed.name ? `"${revealed.name}"` : "New key"} created — copy it now
                </DialogTitle>
                <DialogDescription>
                  This is the only time the full key is shown. We store only a hash.
                </DialogDescription>
              </DialogHeader>
              <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3">
                <code className="flex-1 break-all font-mono text-xs">{revealed.plaintext}</code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void copy(revealed.plaintext)}
                >
                  <Copy />
                  {copied ? "Copied!" : "Copy"}
                </Button>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeCreateDialog}>
                  Done
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Create API key</DialogTitle>
                <DialogDescription>
                  The full key is shown once after creation — store it somewhere safe.
                </DialogDescription>
              </DialogHeader>
              <form
                className="grid gap-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  create.mutate({ type, name });
                }}
              >
                <div className="grid gap-2">
                  <Label>Type</Label>
                  <Select
                    value={type}
                    onValueChange={(value) => setType(value as "secret" | "publishable")}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="secret">Secret (server-side)</SelectItem>
                      <SelectItem value="publishable">Publishable (browser-safe)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="key-name">Name (optional)</Label>
                  <Input
                    id="key-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="production backend"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={closeCreateDialog}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={create.isPending}>
                    {create.isPending ? "Creating…" : "Create key"}
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={editingOrigins !== null}
        onOpenChange={(open) => {
          if (!open) setEditingOrigins(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Allowed origins</DialogTitle>
            <DialogDescription>
              Exact origins allowed to use this key from a browser. Empty = allow all.
            </DialogDescription>
          </DialogHeader>
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              addOrigin();
            }}
          >
            <Input
              type="text"
              value={newOrigin}
              onChange={(e) => setNewOrigin(e.target.value)}
              placeholder="https://example.com"
            />
            <Button type="submit" variant="outline" disabled={!newOrigin.trim()}>
              <Plus />
              Add
            </Button>
          </form>
          <div className="flex min-h-9 flex-wrap items-center gap-2">
            {origins.length === 0 && (
              <span className="text-sm text-muted-foreground">
                No origins — any origin may use this key.
              </span>
            )}
            {origins.map((origin) => (
              <Badge key={origin} variant="secondary" className="gap-1 font-mono text-xs">
                {origin}
                <button
                  type="button"
                  className="ml-0.5 rounded-full hover:text-destructive"
                  onClick={() => setOrigins((prev) => prev.filter((o) => o !== origin))}
                  aria-label={`Remove ${origin}`}
                >
                  <X className="size-3" />
                </button>
              </Badge>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditingOrigins(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={updateOrigins.isPending}
              onClick={() => {
                if (editingOrigins) {
                  updateOrigins.mutate({ keyId: editingOrigins, origins });
                }
              }}
            >
              {updateOrigins.isPending ? "Saving…" : "Save origins"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * The environment's internal publishable key, the one the storefront BFF injects on every
 * proxied `/v1` call. It is an `api_keys` row like the ones in the table, but `apiKeys.list`
 * leaves internal keys out and `apiKeys.revoke` refuses them, so this card is the one place
 * it shows. Read from `storefront.get`, so it follows the environment switcher.
 */
function StorefrontKeyCard() {
  const { query } = useStorefrontConfigQuery();
  const [copied, setCopied] = useState(false);
  if (!query.data) return null;
  const { publishableKey } = query.data.config;

  const copyKey = async () => {
    await navigator.clipboard.writeText(publishableKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Hosted storefront key</CardTitle>
        <CardDescription>
          The publishable key that powers this environment's hosted storefront — injected by the
          storefront proxy, never shown to shoppers. It is not listed below and cannot be revoked
          here.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {publishableKey ? (
          <div className="flex items-center gap-2">
            <Input type="text" readOnly value={publishableKey} className="font-mono text-xs" />
            <Button type="button" variant="outline" onClick={() => void copyKey()}>
              <Copy />
              {copied ? "Copied!" : "Copy"}
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            This environment's storefront has not been provisioned yet.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
